import { useId, useRef } from 'react';
import type { TFunction } from 'i18next';
import DishPicker from './DishPicker';
import { emptyAnswer, headingStyle, pickText } from './surveyLogic';
import type { AnswerState, SurveyDish, SurveyQuestion as Question } from './types';

export type QuestionError = 'required' | 'invalid';

interface Props {
  question: Question;
  answer: AnswerState | undefined;
  error?: QuestionError;
  lang: string;
  defaultLang: string;
  dishes: SurveyDish[];
  today: string;
  t: TFunction;
  onChange: (next: AnswerState) => void;
}

/** SC-1752 — one question. Choices are whole-row targets (>= 44 px) with a mark that does not rely on colour. */
export default function SurveyQuestion({
  question: q,
  answer,
  error,
  lang,
  defaultLang,
  dishes,
  today,
  t,
  onChange,
}: Props) {
  const uid = useId();
  const spaceHandled = useRef(false);
  const labelId = `${uid}-label`;
  const msgId = `${uid}-msg`;
  const a = answer ?? emptyAnswer();
  const tx = (v: Parameters<typeof pickText>[0]) => pickText(v, lang, defaultLang);
  const help = tx(q.help);
  const label = tx(q.label);
  const isChoice = q.type === 'single_choice' || q.type === 'rating' || q.type === 'multi_choice';
  const multi = q.type === 'multi_choice';

  const toggle = (id: string) => {
    if (multi) {
      onChange({
        ...a,
        optionIds: a.optionIds.includes(id) ? a.optionIds.filter((x) => x !== id) : [...a.optionIds, id],
      });
    } else {
      onChange({ ...a, optionIds: a.optionIds.includes(id) ? [] : [id] });
    }
  };

  const fieldClass = `w-full min-h-[44px] box-border border-2 border-solid rounded-btn-sm bg-paper text-ink px-3 py-2 font-mono text-base ${
    error ? 'border-sale-red' : 'border-ink'
  }`;

  return (
    <fieldset
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? msgId : undefined}
      tabIndex={error ? -1 : undefined}
      data-question-id={q.id}
      className={`min-w-0 box-border border-0 p-0 m-0 outline-none ${
        error ? 'border-s-4 border-solid border-sale-red ps-3' : ''
      }`}
    >
      <legend id={labelId} style={headingStyle(lang)} className="font-display text-lg text-ink mb-1 p-0">
        {label}
        {q.required && (
          <>
            <span aria-hidden="true"> *</span>
            <span className="sr-only"> ({t('survey.requiredSr')})</span>
          </>
        )}
      </legend>
      {help && <p className="font-mono text-sm text-ink-mute mb-2">{help}</p>}

      {isChoice && (
        <div className="flex flex-col gap-2 mt-2">
          {(q.options ?? []).map((o) => {
            const selected = a.optionIds.includes(o.id);
            return (
              <div key={o.id}>
                <label
                  className={`flex items-center gap-3 min-h-[44px] px-3 py-2 border-2 border-solid border-ink rounded-btn-sm cursor-pointer focus-within:ring-2 focus-within:ring-ink focus-within:ring-offset-2 ${
                    selected ? 'bg-ink text-cream font-semibold' : 'bg-paper text-ink'
                  }`}
                >
                  <input
                    type={multi ? 'checkbox' : 'radio'}
                    name={q.id}
                    className="sr-only"
                    checked={selected}
                    onChange={() => {
                      // a radio only fires change when it becomes checked; the
                      // un-check of an already-chosen row goes through onClick.
                      if (multi || !selected) toggle(o.id);
                    }}
                    onClick={() => {
                      if (!multi && selected) toggle(o.id);
                    }}
                    onKeyDown={(e) => {
                      // Space on a chosen radio does nothing natively: make it un-choose, like a second tap.
                      if (!multi && selected && e.key === ' ') {
                        e.preventDefault();
                        spaceHandled.current = true;
                        toggle(o.id);
                      }
                    }}
                    onKeyUp={(e) => {
                      // the keyup would otherwise re-check the row we just cleared
                      if (spaceHandled.current && e.key === ' ') {
                        e.preventDefault();
                        spaceHandled.current = false;
                      }
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className={`flex h-6 w-6 shrink-0 items-center justify-center border-2 border-solid ${
                      multi ? 'rounded-btn-sm' : 'rounded-full'
                    } ${selected ? 'border-cream bg-cream text-ink' : 'border-ink'}`}
                  >
                    {selected ? '✓' : ''}
                  </span>
                  <span className="font-mono">{tx(o.label)}</span>
                </label>
                {o.hasText && selected && (
                  <input
                    type="text"
                    maxLength={500}
                    aria-label={`${tx(o.label)} — ${t('survey.optionTextPlaceholder')}`}
                    placeholder={t('survey.optionTextPlaceholder')}
                    value={a.optionTexts[o.id] ?? ''}
                    onChange={(e) =>
                      onChange({ ...a, optionTexts: { ...a.optionTexts, [o.id]: e.target.value } })
                    }
                    className={`${fieldClass} mt-2`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {q.type === 'short_text' && (
        <input
          type="text"
          maxLength={300}
          aria-labelledby={labelId}
          placeholder={t('survey.textPlaceholder')}
          value={a.text}
          onChange={(e) => onChange({ ...a, text: e.target.value })}
          className={fieldClass}
        />
      )}
      {q.type === 'long_text' && (
        <textarea
          maxLength={3000}
          rows={4}
          aria-labelledby={labelId}
          placeholder={t('survey.textPlaceholder')}
          value={a.text}
          onChange={(e) => onChange({ ...a, text: e.target.value })}
          className={fieldClass}
        />
      )}
      {q.type === 'date' && (
        <input
          type="date"
          max={today}
          aria-labelledby={labelId}
          value={a.date}
          onChange={(e) => onChange({ ...a, date: e.target.value })}
          className={fieldClass}
        />
      )}
      {q.type === 'dish' && (
        <DishPicker
          labelId={labelId}
          label={label}
          multiple={q.multiple === true}
          dishes={dishes}
          value={a.dishes}
          pending={a.pending ?? ''}
          onChange={(dishesNext, pending) => onChange({ ...a, dishes: dishesNext, pending })}
          t={t}
        />
      )}

      {error && (
        <p id={msgId} role="alert" className="mt-2 font-mono text-sm font-semibold text-ink">
          <span aria-hidden="true" className="text-sale-red">⚠ </span>
          {error === 'required' ? t('survey.required') : t('survey.invalidQuestion')}
        </p>
      )}
    </fieldset>
  );
}
